import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MdDashboard,
  MdAccountBalance,
  MdPeople,
  MdShoppingCart,
  MdLocalShipping,
  MdInventory,
  MdAccountBalanceWallet,
  MdAttachMoney,
  MdAssessment,
  MdSettings,
  MdExpandMore,
  MdExpandLess,
  MdDescription,
  MdLocalOffer,
  MdPlaylistAdd,
  MdPayment,
  MdWarehouse,
  MdSwapVert,
  MdBuild,
  MdViewList,
  MdEdit,
  MdCompareArrows,
  MdInsertChart,
  MdAccessTime,
  MdCalculate,
  MdReceipt,
  MdFlag,
  MdShowChart,
  MdMoneyOff,
  MdBook,
  MdPerson,
  MdBusiness,
  MdCalendarToday,
  MdMenu
} from 'react-icons/md';
import './Menu.css';

interface MenuItem {
  title: string;
  path?: string;
  icon: React.ReactNode;
  subMenu?: MenuItem[];
}

const Menu: React.FC = () => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const menuItems: MenuItem[] = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: <MdDashboard />
    },
    {
      title: 'Master Data',
      icon: <MdAccountBalance />,
      subMenu: [
        { title: 'Chart of Accounts', path: '/master/accounts', icon: <MdAccountBalance /> },
        { title: 'Customers', path: '/master/customers', icon: <MdPeople /> },
        { title: 'Vendors', path: '/master/vendors', icon: <MdLocalShipping /> },
        { title: 'Items', path: '/master/items', icon: <MdInventory /> },
        { title: 'Employees', path: '/master/employees', icon: <MdPeople /> },
        { title: 'Bank Accounts', path: '/master/bank-accounts', icon: <MdAccountBalanceWallet /> }
      ]
    },
    {
      title: 'Sales / AR',
      icon: <MdShoppingCart />,
      subMenu: [
        { title: 'Sales Quotations', path: '/sales/quotations', icon: <MdLocalOffer /> },
        { title: 'Sales Orders', path: '/sales/orders', icon: <MdShoppingCart /> },
        { title: 'Delivery Receipts', path: '/sales/deliveries', icon: <MdLocalShipping /> },
        { title: 'Sales Invoices', path: '/sales/invoices', icon: <MdDescription /> },
        { title: 'Collections', path: '/sales/collections', icon: <MdAttachMoney /> },
        { title: 'AR Aging Report', path: '/sales/ar-aging', icon: <MdAssessment /> }
      ]
    },
    {
      title: 'Purchases / AP',
      icon: <MdLocalShipping />,
      subMenu: [
        { title: 'Purchase Orders', path: '/purchases/orders', icon: <MdPlaylistAdd /> },
        { title: 'Goods Receipt', path: '/purchases/receipts', icon: <MdInventory /> },
        { title: 'Purchase Invoices', path: '/purchases/invoices', icon: <MdDescription /> },
        { title: 'Payments', path: '/purchases/payments', icon: <MdPayment /> },
        { title: 'AP Aging Report', path: '/purchases/ap-aging', icon: <MdAssessment /> }
      ]
    },
    {
      title: 'Inventory',
      icon: <MdInventory />,
      subMenu: [
        { title: 'Stock Status', path: '/inventory/status', icon: <MdWarehouse /> },
        { title: 'Stock In/Out', path: '/inventory/transactions', icon: <MdSwapVert /> },
        { title: 'Stock Adjustment', path: '/inventory/adjustment', icon: <MdBuild /> },
        { title: 'BOM (Bill of Materials)', path: '/inventory/bom', icon: <MdViewList /> }
      ]
    },
    {
      title: 'General Ledger',
      icon: <MdAccountBalanceWallet />,
      subMenu: [
        { title: 'Journal Entry', path: '/gl/journal-entry', icon: <MdEdit /> },
        { title: 'Bank Reconciliation', path: '/gl/bank-reconciliation', icon: <MdCompareArrows /> },
        { title: 'Financial Statements', path: '/gl/statements', icon: <MdInsertChart /> }
      ]
    },
    {
      title: 'Payroll',
      icon: <MdAttachMoney />,
      subMenu: [
        { title: 'Timekeeping', path: '/payroll/timekeeping', icon: <MdAccessTime /> },
        { title: 'Payroll Processing', path: '/payroll/processing', icon: <MdCalculate /> },
        { title: 'Payroll Register', path: '/payroll/register', icon: <MdReceipt /> },
        { title: 'Government Reports', path: '/payroll/gov-reports', icon: <MdFlag /> }
      ]
    },
    {
      title: 'Reports',
      icon: <MdAssessment />,
      subMenu: [
        { title: 'Sales Reports', path: '/reports/sales', icon: <MdShowChart /> },
        { title: 'Expense Reports', path: '/reports/expense', icon: <MdMoneyOff /> },
        { title: 'Tax Reports', path: '/reports/tax', icon: <MdReceipt /> },
        { title: 'General Ledger Reports', path: '/reports/gl', icon: <MdBook /> }
      ]
    },
    {
      title: 'Settings',
      icon: <MdSettings />,
      subMenu: [
        { title: 'User Management', path: '/settings/users', icon: <MdPerson /> },
        { title: 'Company Profile', path: '/settings/company', icon: <MdBusiness /> },
        { title: 'Fiscal Year Setup', path: '/settings/fiscal-year', icon: <MdCalendarToday /> },
        { title: 'Menu Setup', path: '/settings/menu', icon: <MdMenu /> }
      ]
    }
  ];

  // 현재 경로를 기반으로 어떤 메뉴가 열려야 하는지 찾기
  useEffect(() => {
    const currentPath = location.pathname;
    const openMenus: string[] = [];

    menuItems.forEach(menuItem => {
      if (menuItem.subMenu) {
        const hasActiveSubMenu = menuItem.subMenu.some(subItem =>
          subItem.path === currentPath
        );
        if (hasActiveSubMenu) {
          openMenus.push(menuItem.title);
        }
      }
    });

    // 현재 경로에 해당하는 메뉴만 열기
    setExpandedMenus(openMenus);
  }, [location.pathname]);

  const toggleMenu = (title: string) => {
    setExpandedMenus(prev =>
      prev.includes(title)
        ? prev.filter(item => item !== title)
        : [...prev, title]
    );
  };

  const isActive = (path: string) => location.pathname === path;

  const renderMenuItem = (item: MenuItem, depth: number = 0) => {
    const hasSubMenu = item.subMenu && item.subMenu.length > 0;
    const isExpanded = expandedMenus.includes(item.title);

    if (hasSubMenu) {
      return (
        <li key={item.title} className="menu-item">
          <div
            className={`menu-link ${depth > 0 ? 'submenu-item' : ''}`}
            onClick={() => toggleMenu(item.title)}
          >
            <span className="menu-icon">{item.icon}</span>
            <span className="menu-title">{item.title}</span>
            <span className="menu-arrow">
              {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
            </span>
          </div>
          {isExpanded && (
            <ul className="submenu">
              {item.subMenu?.map(subItem => renderMenuItem(subItem, depth + 1))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.title} className="menu-item">
        <Link
          to={item.path!}
          className={`menu-link ${depth > 0 ? 'submenu-item' : ''} ${isActive(item.path!) ? 'active' : ''}`}
        >
          <span className="menu-icon">{item.icon}</span>
          <span className="menu-title">{item.title}</span>
        </Link>
      </li>
    );
  };

  return (
    <nav className="sidebar-menu">
      <div className="sidebar-header">
        <h4>DEWFOAM</h4>
        <p>Accounting System</p>
      </div>
      <ul className="menu-list">
        {menuItems.map(item => renderMenuItem(item))}
      </ul>
    </nav>
  );
};

export default Menu;
